import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TOXBOX_API_BASE, TOXBOX_PAGE_SIZE } from "./toxboxConfig";
import { Search, X } from "lucide-react";
import styles from "./Blog.module.css";

// Helper — Format ISO date
function formatDate(isoString) {
  if (!isoString) return "";
  try {
    return new Date(isoString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// Skeleton card loader
function SkeletonCard() {
  return (
    <div className={styles.skeletonCard} aria-hidden="true">
      <div className={styles.skeletonThumb} />
      <div className={styles.skeletonBody}>
        <div className={styles.skeletonPill} />
        <div className={styles.skeletonTitle} />
        <div className={styles.skeletonLine} />
        <div className={styles.skeletonLine} style={{ width: "70%" }} />
        <div className={styles.skeletonMeta} />
      </div>
    </div>
  );
}

// Blog post card
function BlogCard({ blog, onClick }) {
  const hasThumbnail = blog.thumbnailUrl && blog.thumbnailUrl.trim() !== "";

  return (
    <article
      className={styles.blogCard}
      onClick={() => onClick(blog.slug)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick(blog.slug)}
      aria-label={`Read blog: ${blog.title}`}
    >
      <div className={styles.cardThumb}>
        {hasThumbnail ? (
          <img
            src={blog.thumbnailUrl}
            alt={blog.title}
            className={styles.thumbImg}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.nextSibling?.style?.removeProperty("display");
            }}
          />
        ) : null}
        <div
          className={styles.thumbFallback}
          style={hasThumbnail ? { display: "none" } : {}}
        >
          <span className={styles.thumbFallbackIcon}>📝</span>
        </div>
      </div>

      <div className={styles.cardBody}>
        {blog.industry && (
          <span className={styles.industryPill}>{blog.industry}</span>
        )}

        <h3 className={styles.cardTitle}>{blog.title}</h3>

        <p className={styles.cardDesc}>
          {blog.metaDescription || "Read the full article for insights and tips."}
        </p>

        <div className={styles.cardMeta}>
          <span>{formatDate(blog.publishedAt)}</span>
        </div>
      </div>
    </article>
  );
}

// Pagination
function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const left = currentPage - delta;
    const right = currentPage + delta;
    let start = Math.max(1, left);
    let end = Math.min(totalPages, right);

    if (start > 1) range.push(1, start > 2 ? "..." : null);
    for (let i = start; i <= end; i++) range.push(i);
    if (end < totalPages) range.push(end < totalPages - 1 ? "..." : null, totalPages);

    return range.filter(Boolean);
  };

  return (
    <nav className={styles.pagination} aria-label="Blog pagination">
      <button
        className={styles.pageBtn}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        ← Prev
      </button>

      {getPageNumbers().map((num, idx) =>
        num === "..." ? (
          <span key={`ellipsis-${idx}`} className={styles.pageEllipsis}>
            …
          </span>
        ) : (
          <button
            key={`page-${num}`}
            className={`${styles.pageBtn} ${currentPage === num ? styles.pageBtnActive : ""}`}
            onClick={() => onPageChange(num)}
            aria-current={currentPage === num ? "page" : undefined}
          >
            {num}
          </button>
        )
      )}

      <button
        className={styles.pageBtn}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        Next →
      </button>
    </nav>
  );
}

export default function BlogListPage() {
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const filteredBlogs = blogs.filter((blog) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const titleMatch = blog.title.toLowerCase().includes(query);
    const descMatch = (blog.metaDescription || "").toLowerCase().includes(query);
    const keywordMatch = (blog.keywords || []).some(k => k.toLowerCase().includes(query));
    return titleMatch || descMatch || keywordMatch;
  });

  const fetchBlogs = useCallback(async (page = 1, industry = "") => {
    setLoading(true);
    setError("");

    try {
      let url = `${TOXBOX_API_BASE}?page=${page}&limit=${TOXBOX_PAGE_SIZE}`;
      if (industry) url += `&industry=${encodeURIComponent(industry)}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(url, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const resJson = await response.json();
      const { data, pagination, metadata } = resJson;

      setBlogs(data || []);
      setCurrentPage(pagination?.page || 1);
      setTotalPages(pagination?.totalPages || 1);
      setTotalCount(pagination?.total || 0);

      if (!industry && metadata?.availableFilters?.industries?.length > 0) {
        setIndustries(metadata.availableFilters.industries);
      }
    } catch (err) {
      console.error("[BlogListPage] Failed to fetch blogs:", err);
      if (err.name === "AbortError") {
        setError("The request timed out. Please check your connection.");
      } else {
        setError("Failed to load blogs. Please check your connection and try again.");
      }
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogs(1, "");
  }, [fetchBlogs]);

  const handleCardClick = (slug) => {
    const selectedBlog = blogs.find((b) => b.slug === slug);
    navigate(`/blog/${slug}`, { state: { blog: selectedBlog } });
  };

  const handleIndustrySelect = (industry) => {
    const newIndustry = industry === selectedIndustry ? "" : industry;
    setSelectedIndustry(newIndustry);
    fetchBlogs(1, newIndustry);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchBlogs(page, selectedIndustry);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderSkeletons = () =>
    Array.from({ length: TOXBOX_PAGE_SIZE }).map((_, i) => (
      <SkeletonCard key={i} />
    ));

  const renderError = () => (
    <div className={styles.errorState}>
      <div className={styles.errorIcon}>⚠️</div>
      <h3 className={styles.errorTitle}>Couldn't load blogs</h3>
      <p className={styles.errorMessage}>{error}</p>
      <button
        className={styles.retryBtn}
        onClick={() => fetchBlogs(currentPage, selectedIndustry)}
      >
        Try Again
      </button>
    </div>
  );

  const renderEmpty = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>📭</div>
      <h3 className={styles.emptyTitle}>No blogs found</h3>
      <p className={styles.emptyMessage}>
        {selectedIndustry
          ? `No published blogs in the "${selectedIndustry}" category yet.`
          : "No published blogs available yet. Check back soon!"}
      </p>
      {selectedIndustry && (
        <button
          className={styles.retryBtn}
          onClick={() => handleIndustrySelect("")}
        >
          Clear Filter
        </button>
      )}
    </div>
  );

  const renderSearchEmpty = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>🔍</div>
      <h3 className={styles.emptyTitle}>No matching articles</h3>
      <p className={styles.emptyMessage}>
        We couldn't find any articles matching "{searchQuery}". Try adjusting your keywords.
      </p>
      <button
        className={styles.retryBtn}
        onClick={() => setSearchQuery("")}
      >
        Clear Search
      </button>
    </div>
  );

  return (
    <div className={styles.pageWrapper}>
      <header className={styles.pageHeader}>
        <p className={styles.headerEyebrow}>Knowledge Hub</p>
        <h1 className={styles.pageTitle}>Insights &amp; Resources</h1>
        <p className={styles.pageSubtitle}>
          Hiring trends, industry news, and expert guidance from the Provaantech team.
        </p>
      </header>

      <div className={styles.contentContainer}>
        {!error && (
          <div className={styles.controlsRow}>
            <div className={styles.searchWrapper}>
              <span className={styles.searchIcon}>
                <Search size={18} />
              </span>
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className={styles.clearSearchBtn}
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {!loading && industries.length > 0 && (
              <div className={styles.filterRow} role="group" aria-label="Filter by industry">
                <button
                  className={`${styles.filterPill} ${selectedIndustry === "" ? styles.filterPillActive : ""}`}
                  onClick={() => handleIndustrySelect("")}
                >
                  All
                </button>

                {industries.map((ind) => (
                  <button
                    key={ind}
                    className={`${styles.filterPill} ${selectedIndustry === ind ? styles.filterPillActive : ""}`}
                    onClick={() => handleIndustrySelect(ind)}
                  >
                    {ind}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && !error && filteredBlogs.length > 0 && (
          <p className={styles.resultCount}>
            {searchQuery
              ? `Showing ${filteredBlogs.length} matching result${filteredBlogs.length === 1 ? "" : "s"}`
              : selectedIndustry
              ? `Showing ${blogs.length} of ${totalCount} articles in "${selectedIndustry}"`
              : `Showing ${blogs.length} of ${totalCount} articles`}
          </p>
        )}

        {loading ? (
          <div className={styles.blogGrid}>{renderSkeletons()}</div>
        ) : error ? (
          renderError()
        ) : blogs.length === 0 ? (
          renderEmpty()
        ) : filteredBlogs.length === 0 ? (
          renderSearchEmpty()
        ) : (
          <>
            <div className={styles.blogGrid}>
              {filteredBlogs.map((blog) => (
                <BlogCard key={blog._id} blog={blog} onClick={handleCardClick} />
              ))}
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
    </div>
  );
}
